import React, { useEffect, useState, useRef, useCallback } from "react";
import { db, functions } from "../firebase";
import {
  collection, addDoc, query, where, onSnapshot,
  deleteDoc, doc, updateDoc, setDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

const openaiChat = httpsCallable(functions, "openaiChat");

// ── Standard-Einstellungen ────────────────────────────────────────────────────
const DEFAULT_SETTINGS = {
  grundbetrag: 5.00,
  fehlenUnentsch: 5.00,
  pumpen: 0.10,
  klingeln: 0.50,
  verloreneSpiele: 0.50,
  kugelBringen: 0.50,
  gewarnt: 1.00,
  zuSpaet: 2.00,      // € pro 15 min
  rundenbetrag: 15.00, // € pro Runde
};

const MITGLIEDER_DEFAULT = [
  { name: "Alli",     von: "1998-11-14", bis: "" },
  { name: "Jutta D.", von: "1998-11-14", bis: "" },
  { name: "Jutta W.", von: "1998-11-14", bis: "" },
  { name: "Silvia",   von: "1998-11-14", bis: "" },
  { name: "Stefan",   von: "1998-11-14", bis: "" },
  { name: "Felix",    von: "1998-11-14", bis: "" },
  { name: "Pichel",   von: "1998-11-14", bis: "" },
  { name: "Keily",    von: "1998-11-14", bis: "" },
  { name: "Anne",     von: "1998-11-14", bis: "" },
  { name: "Hubert",   von: "1998-11-14", bis: "" },
  { name: "Claudia",  von: "1998-11-14", bis: "" },
  { name: "Marion",   von: "1998-11-14", bis: "" },
  { name: "Sascha",   von: "1998-11-14", bis: "" },
  { name: "Summi",    von: "1998-11-14", bis: "" },
];

// Saison: Feb = neues Kegeljahr. Januar gehört noch zur Vorsaison.
function getSaison(datum) {
  const d = new Date(datum);
  const monat = d.getMonth() + 1;
  const jahr = d.getFullYear();
  return monat === 1 ? `${jahr - 1}/${jahr}` : `${jahr}/${jahr + 1}`;
}

// Ist ein Mitglied an einem gegebenen Datum aktiv?
function isAktivAm(mitglied, datum) {
  if (!datum) return !mitglied.bis;
  const d = datum.slice(0, 10);
  const vonOk = !mitglied.von || d >= mitglied.von;
  const bisOk = !mitglied.bis || d <= mitglied.bis;
  return vonOk && bisOk;
}

function leerEintrag(name) {
  return {
    name,
    status: "anwesend", // anwesend | unentsch | entsch | abwesend
    pumpen: 0, klingeln: 0, verloreneSpiele: 0,
    lustwurf: 0, kugelBringen: 0, schnappszahl: 0,
    alleNeune: 0, gewarnt: 0, zuSpaet: 0,
    kugelFallen: 0, kugelEinholen: 0,
    holzzahl: "", uebertragRunden: 0, neueRunden: 0,
    offeneRunden: 0, bezahlt: "", uebertrag: "",
  };
}

function berechneGeld(e, settings = DEFAULT_SETTINGS) {
  if (e.status === "abwesend") return 0;
  let b = settings.grundbetrag;
  if (e.status === "unentsch") b += settings.fehlenUnentsch;
  b += (Number(e.pumpen) || 0) * settings.pumpen;
  b += (Number(e.klingeln) || 0) * settings.klingeln;
  b += (Number(e.verloreneSpiele) || 0) * settings.verloreneSpiele;
  b += (Number(e.kugelBringen) || 0) * settings.kugelBringen;
  b += (Number(e.gewarnt) || 0) * settings.gewarnt;
  b += (Number(e.zuSpaet) || 0) * settings.zuSpaet;
  return Math.round(b * 100) / 100;
}

function berechneNeueRunden(e) {
  if (e.status === "abwesend") return 0;
  return Math.round((
    (Number(e.lustwurf) || 0) * 0.25 +
    (Number(e.schnappszahl) || 0) * 0.5 +
    (Number(e.alleNeune) || 0) * 0.5 +
    (Number(e.kugelFallen) || 0) * 1 +
    (Number(e.kugelEinholen) || 0) * 0.5
  ) * 100) / 100;
}

// ── Filter-Hilfsfunktionen ────────────────────────────────────────────────────
const MONATE = ["Jan","Feb","Mär","Apr","Mai","Jun","Jul","Aug","Sep","Okt","Nov","Dez"];

function datumImBereich(datum, von, bis) {
  if (!datum) return false;
  const d = new Date(datum);
  const dVal = d.getFullYear() * 100 + d.getMonth() + 1;
  return dVal >= von.jahr * 100 + von.monat && dVal <= bis.jahr * 100 + bis.monat;
}

function ladeFilterAusStorage() {
  try {
    const raw = localStorage.getItem("kegeln_filter");
    if (raw) return JSON.parse(raw);
  } catch {}
  const jetzt = new Date();
  const monat = jetzt.getMonth() + 1;
  const jahr = jetzt.getFullYear();
  const saisonStart = monat === 1 ? jahr - 1 : jahr;
  return { von: { monat: 2, jahr: saisonStart }, bis: { monat: 1, jahr: saisonStart + 1 } };
}

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  page: { padding: "1rem", width: "100%", boxSizing: "border-box" },
  card: { backgroundColor: "white", borderRadius: "10px", padding: "1rem", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", marginBottom: "0.8rem" },
  tab: { padding: "0.5rem 1rem", border: "1px solid #c5d8ec", borderRadius: "6px", background: "white", cursor: "pointer", fontSize: "0.88rem", fontWeight: 500, color: "#555" },
  tabAktiv: { backgroundColor: "#1a3558", color: "white", borderColor: "#1a3558" },
  btn: { padding: "0.4rem 0.9rem", backgroundColor: "#1a73e8", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "0.85rem", fontWeight: 500 },
  btnGreen: { padding: "0.4rem 0.9rem", backgroundColor: "#34a853", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "0.85rem" },
  btnSm: { padding: "0.25rem 0.6rem", backgroundColor: "#f1f3f4", border: "1px solid #dadce0", borderRadius: "5px", cursor: "pointer", fontSize: "0.8rem" },
  btnDanger: { padding: "0.25rem 0.6rem", backgroundColor: "#fce8e6", border: "1px solid #ea4335", borderRadius: "5px", cursor: "pointer", fontSize: "0.8rem", color: "#c5221f" },
  input: { padding: "0.35rem 0.5rem", border: "1px solid #dadce0", borderRadius: "5px", fontSize: "0.85rem", outline: "none", boxSizing: "border-box" },
  select: { padding: "0.35rem 0.4rem", border: "1px solid #dadce0", borderRadius: "5px", fontSize: "0.85rem" },
  label: { fontSize: "0.72rem", color: "#666", display: "block", marginBottom: "0.15rem" },
  h2: { fontWeight: 700, color: "#1a3558", fontSize: "1.1rem", marginBottom: "0.7rem" },
  tag: { fontSize: "0.75rem", padding: "0.15rem 0.5rem", borderRadius: "999px", backgroundColor: "#e8f0fe", color: "#1a73e8", fontWeight: 600, whiteSpace: "nowrap" },
};

const thStyle = { backgroundColor: "#1a3558", color: "white", padding: "0.4rem 0.5rem", fontSize: "0.75rem", textAlign: "center", whiteSpace: "nowrap", position: "sticky", top: 0 };
const tdStyle = { padding: "0.3rem 0.4rem", fontSize: "0.82rem", textAlign: "center", borderBottom: "1px solid #f0f0f0", verticalAlign: "middle" };

// ── Nummerneingabe ────────────────────────────────────────────────────────────
function Num({ value, onChange, disabled, width = "44px" }) {
  return (
    <input type="number" min="0" value={value || 0} disabled={disabled}
      onChange={(e) => onChange(Number(e.target.value))}
      style={{ ...s.input, width, padding: "0.2rem 0.3rem", textAlign: "center", backgroundColor: disabled ? "#f8f8f8" : "white" }} />
  );
}

// ── Kegelzettel-Zeile ─────────────────────────────────────────────────────────
function MitgliedZeile({ eintrag, idx, onChange, settings }) {
  const e = eintrag;
  const ab = e.status === "abwesend";
  const geld = berechneGeld(e, settings);
  const runden = berechneNeueRunden(e);
  const offene = (Number(e.uebertragRunden) || 0) + runden;
  const rundenEuro = Math.round(offene * settings.rundenbetrag * 100) / 100;

  const set = useCallback((field, val) => {
    const neu = { ...e, [field]: val };
    neu.neueRunden = berechneNeueRunden(neu);
    neu.offeneRunden = (Number(neu.uebertragRunden) || 0) + neu.neueRunden;
    onChange(idx, neu);
  }, [e, idx, onChange]);

  const statusColor = { anwesend: "white", unentsch: "#fce8e6", entsch: "#e8f5e9", abwesend: "#f8f8f8" }[e.status];

  return (
    <tr style={{ backgroundColor: statusColor }}>
      <td style={{ ...tdStyle, fontWeight: 600, textAlign: "left", paddingLeft: "0.7rem", whiteSpace: "nowrap" }}>{e.name}</td>
      <td style={tdStyle}>
        <select style={{ ...s.select, fontSize: "0.75rem", width: "105px" }} value={e.status} onChange={(ev) => set("status", ev.target.value)}>
          <option value="anwesend">Anwesend</option>
          <option value="unentsch">Fehlt unentsch.</option>
          <option value="entsch">Fehlt entsch.</option>
          <option value="abwesend">Kein Mitglied</option>
        </select>
      </td>
      <td style={tdStyle}><Num value={e.pumpen} disabled={ab} onChange={(v) => set("pumpen", v)} /></td>
      <td style={tdStyle}><Num value={e.klingeln} disabled={ab} onChange={(v) => set("klingeln", v)} /></td>
      <td style={tdStyle}><Num value={e.verloreneSpiele} disabled={ab} onChange={(v) => set("verloreneSpiele", v)} /></td>
      <td style={tdStyle}><Num value={e.kugelBringen} disabled={ab} onChange={(v) => set("kugelBringen", v)} /></td>
      <td style={tdStyle}><Num value={e.gewarnt} disabled={ab} onChange={(v) => set("gewarnt", v)} /></td>
      <td style={tdStyle}><Num value={e.zuSpaet} disabled={ab} onChange={(v) => set("zuSpaet", v)} width="50px" /></td>
      {/* Runden */}
      <td style={{ ...tdStyle, backgroundColor: ab ? "#f8f8f8" : "#f0f7ff" }}><Num value={e.lustwurf} disabled={ab} onChange={(v) => set("lustwurf", v)} /></td>
      <td style={{ ...tdStyle, backgroundColor: ab ? "#f8f8f8" : "#f0f7ff" }}><Num value={e.schnappszahl} disabled={ab} onChange={(v) => set("schnappszahl", v)} /></td>
      <td style={{ ...tdStyle, backgroundColor: ab ? "#f8f8f8" : "#f0f7ff" }}><Num value={e.alleNeune} disabled={ab} onChange={(v) => set("alleNeune", v)} /></td>
      <td style={{ ...tdStyle, backgroundColor: ab ? "#f8f8f8" : "#f0f7ff" }}><Num value={e.kugelFallen} disabled={ab} onChange={(v) => set("kugelFallen", v)} /></td>
      <td style={{ ...tdStyle, backgroundColor: ab ? "#f8f8f8" : "#f0f7ff" }}><Num value={e.kugelEinholen} disabled={ab} onChange={(v) => set("kugelEinholen", v)} /></td>
      <td style={{ ...tdStyle, backgroundColor: "#e8f5e9", fontWeight: 600 }}>{ab ? "—" : runden.toFixed(2)}</td>
      <td style={{ ...tdStyle, backgroundColor: "#e8f5e9" }}>
        <input style={{ ...s.input, width: "50px", padding: "0.2rem", textAlign: "center" }}
          type="number" step="0.25" min="0" value={e.uebertragRunden || 0} disabled={ab}
          onChange={(ev) => set("uebertragRunden", Number(ev.target.value))} />
      </td>
      <td style={{ ...tdStyle, backgroundColor: "#e8f5e9", fontWeight: 600, color: offene > 0 ? "#c5221f" : "#333" }}>{ab ? "—" : offene.toFixed(2)}</td>
      <td style={{ ...tdStyle, backgroundColor: "#fff3cd", fontSize: "0.78rem", color: "#b45309" }}>{ab ? "—" : `${rundenEuro.toFixed(2)} €`}</td>
      <td style={tdStyle}>
        <input style={{ ...s.input, width: "52px", padding: "0.2rem", textAlign: "center" }} type="number"
          value={e.holzzahl || ""} disabled={ab} onChange={(ev) => set("holzzahl", ev.target.value)} />
      </td>
      <td style={{ ...tdStyle, fontWeight: 700, color: "#1a3558" }}>{ab ? "—" : `${geld.toFixed(2)} €`}</td>
      <td style={tdStyle}>
        <input style={{ ...s.input, width: "70px", padding: "0.2rem", textAlign: "right" }}
          placeholder="0,00 €" value={e.bezahlt} disabled={ab}
          onChange={(ev) => set("bezahlt", ev.target.value)} />
      </td>
      <td style={{ ...tdStyle, fontStyle: "italic", color: "#888" }}>{e.uebertrag || "—"}</td>
    </tr>
  );
}

// ── Kegelzettel Detail-Ansicht ────────────────────────────────────────────────
function KegelzettelDetail({ session, onSpeichern, onZurueck, settings }) {
  const [data, setData] = useState({ ...session });
  const [gespeichert, setGespeichert] = useState(true);
  const druckRef = useRef();

  const eintraegeAktualisieren = useCallback((idx, neu) => {
    setData((d) => {
      const ei = [...d.eintraege];
      ei[idx] = neu;
      return { ...d, eintraege: ei };
    });
    setGespeichert(false);
  }, []);

  const speichern = async () => {
    await onSpeichern(data);
    setGespeichert(true);
  };

  const drucken = () => {
    const datumStr = data.datum ? new Date(data.datum).toLocaleDateString("de-DE") : "";
    const aktiveMitglieder = (data.eintraege || []).filter((e) => e.status !== "abwesend");
    // Längster Name für Spaltenbreite
    const maxNameLen = Math.max(...aktiveMitglieder.map((e) => e.name.length), 8);
    const nameBreite = Math.min(Math.max(maxNameLen * 7, 60), 120);

    const zeilen = aktiveMitglieder.map((e) => {
      const geld = berechneGeld(e, settings);
      const runden = berechneNeueRunden(e);
      const offene = (Number(e.uebertragRunden) || 0) + runden;
      const rundenEuro = Math.round(offene * settings.rundenbetrag * 100) / 100;
      return `<tr>
        <td style="border:1px solid #333;padding:3px 5px;font-size:10px;font-weight:bold;width:${nameBreite}px">${e.name}</td>
        <td style="border:1px solid #333;padding:3px 5px;font-size:10px;text-align:center">${e.status === "unentsch" ? "ue" : e.status === "entsch" ? "e" : ""}</td>
        <td style="border:1px solid #333;padding:3px 5px;font-size:10px;text-align:center">${e.pumpen || ""}</td>
        <td style="border:1px solid #333;padding:3px 5px;font-size:10px;text-align:center">${e.klingeln || ""}</td>
        <td style="border:1px solid #333;padding:3px 5px;font-size:10px;text-align:center">${e.verloreneSpiele || ""}</td>
        <td style="border:1px solid #333;padding:3px 5px;font-size:10px;text-align:center">${e.kugelBringen || ""}</td>
        <td style="border:1px solid #333;padding:3px 5px;font-size:10px;text-align:center">${e.gewarnt || ""}</td>
        <td style="border:1px solid #333;padding:3px 5px;font-size:10px;text-align:center">${e.zuSpaet || ""}</td>
        <td style="border:1px solid #333;padding:3px 5px;font-size:10px;text-align:center">${e.lustwurf || ""}</td>
        <td style="border:1px solid #333;padding:3px 5px;font-size:10px;text-align:center">${e.schnappszahl || ""}</td>
        <td style="border:1px solid #333;padding:3px 5px;font-size:10px;text-align:center">${e.alleNeune || ""}</td>
        <td style="border:1px solid #333;padding:3px 5px;font-size:10px;text-align:center">${e.kugelFallen || ""}</td>
        <td style="border:1px solid #333;padding:3px 5px;font-size:10px;text-align:center">${e.kugelEinholen || ""}</td>
        <td style="border:1px solid #333;padding:3px 5px;font-size:10px;text-align:center">${offene > 0 ? offene.toFixed(2) : ""}</td>
        <td style="border:1px solid #333;padding:3px 5px;font-size:10px;text-align:center">${e.holzzahl || ""}</td>
        <td style="border:1px solid #333;padding:3px 5px;font-size:10px;text-align:right;font-weight:bold">${geld.toFixed(2)} €</td>
        <td style="border:1px solid #333;padding:3px 5px;font-size:10px;text-align:center">${e.bezahlt || ""}</td>
        <td style="border:1px solid #333;padding:3px 5px;font-size:10px;width:50px"></td>
      </tr>`;
    }).join("");

    const strafen = [
      `Grundbetrag: ${settings.grundbetrag.toFixed(2)} €`,
      `Fehlen unentsch.: +${settings.fehlenUnentsch.toFixed(2)} €`,
      `Pumpen: ${settings.pumpen.toFixed(2)} €`,
      `Klingeln: ${settings.klingeln.toFixed(2)} €`,
      `Verl. Spiele: ${settings.verloreneSpiele.toFixed(2)} €`,
      `Kug. bringen: ${settings.kugelBringen.toFixed(2)} €`,
      `Gewarnt: ${settings.gewarnt.toFixed(2)} €`,
      `Zu spät: ${settings.zuSpaet.toFixed(2)} €/15min`,
      `Runde: ${settings.rundenbetrag.toFixed(2)} €`,
    ].join(" · ");

    const fenster = window.open("", "_blank");
    fenster.document.write(`<html><head><title>Kegelzettel ${datumStr}</title>
      <style>
        @page { size: A4 landscape; margin: 1cm }
        body { font-family: Arial, sans-serif; font-size: 10px; margin: 0 }
        table { border-collapse: collapse; width: 100% }
        th { background: #1a3558; color: white; font-size: 9px; border: 1px solid #333; padding: 3px 4px; text-align: center }
        .header { display: flex; justify-content: space-between; margin-bottom: 6px }
        .strafen { margin-top: 6px; font-size: 8px; color: #555; border-top: 1px solid #ccc; padding-top: 4px }
        .meta { font-size: 9px; margin-top: 5px; display: flex; gap: 20px }
      </style></head><body>
      <div class="header">
        <strong>🎳 Kegelauswertung — ${datumStr}</strong>
        <span>Saison ${getSaison(data.datum || new Date().toISOString())}</span>
      </div>
      <table>
        <thead><tr>
          <th>Name</th><th>Fehlen</th>
          <th>Pumpen<br>${settings.pumpen.toFixed(2)}€</th>
          <th>Klingeln<br>${settings.klingeln.toFixed(2)}€</th>
          <th>Verl.Spl.<br>${settings.verloreneSpiele.toFixed(2)}€</th>
          <th>Kug.bring.<br>${settings.kugelBringen.toFixed(2)}€</th>
          <th>Gewarnt<br>${settings.gewarnt.toFixed(2)}€</th>
          <th>Zu spät<br>${settings.zuSpaet.toFixed(2)}€</th>
          <th>Lustwurf<br>¼R</th>
          <th>Schnaps<br>½R</th>
          <th>Alle 9<br>½R</th>
          <th>Kug.fall.<br>1R</th>
          <th>Kug.einh.<br>½R</th>
          <th>Off.Runden<br>(${settings.rundenbetrag.toFixed(0)}€/R)</th>
          <th>Holz</th>
          <th>Gesamt €</th>
          <th>Bezahlt</th>
          <th>Übertrag</th>
        </tr></thead>
        <tbody>${zeilen}</tbody>
      </table>
      <div class="meta">
        <span>Kegelmotto: ${data.motto || "_______________"}</span>
        <span>Kegelmutti: ${data.kegelmutti || "_______________"}</span>
        <span>Kassiererin: ${data.kassiererin || "_______________"}</span>
        <span>Schriftführer: ${data.schriftfuehrer || "_______________"}</span>
        <span>Rundenschreier: ${data.rundenschreier || "_______________"}</span>
      </div>
      <div class="strafen">${strafen}</div>
      </body></html>`);
    fenster.document.close();
    fenster.print();
  };

  const sumGeld = (data.eintraege || []).reduce((sum, e) => sum + berechneGeld(e, settings), 0);

  return (
    <div>
      <div style={{ display: "flex", gap: "0.7rem", alignItems: "center", marginBottom: "1rem", flexWrap: "wrap" }}>
        <button style={s.btnSm} onClick={onZurueck}>← Zurück</button>
        <div style={s.h2}>
          🎳 Kegelzettel {data.datum ? new Date(data.datum).toLocaleDateString("de-DE") : "—"}
          <span style={{ ...s.tag, marginLeft: "0.5rem" }}>{getSaison(data.datum || new Date().toISOString())}</span>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: "0.5rem" }}>
          {!gespeichert && <span style={{ fontSize: "0.75rem", color: "#e67700", alignSelf: "center" }}>● ungespeichert</span>}
          <button style={s.btnGreen} onClick={speichern}>💾 Speichern</button>
          <button style={s.btn} onClick={drucken}>🖨️ A4 Quer drucken</button>
        </div>
      </div>

      {/* Metadaten */}
      <div style={{ ...s.card, display: "flex", gap: "0.8rem", flexWrap: "wrap" }}>
        {[["Datum","datum","date"],["Kegelmotto","motto","text"],["Kegelmutti","kegelmutti","text"],["Kassiererin","kassiererin","text"],["Schriftführer","schriftfuehrer","text"],["Rundenschreier","rundenschreier","text"]].map(([label, field, type]) => (
          <div key={field} style={{ minWidth: "120px" }}>
            <label style={s.label}>{label}</label>
            <input style={{ ...s.input, width: "100%" }} type={type} value={data[field] || ""}
              onChange={(e) => { setData((d) => ({ ...d, [field]: e.target.value })); setGespeichert(false); }} />
          </div>
        ))}
      </div>

      {/* Tabelle */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", backgroundColor: "white", borderRadius: "8px", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, textAlign: "left", paddingLeft: "0.7rem" }}>Name</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle} title={`Pumpen: ${settings.pumpen.toFixed(2)}€`}>Pumpen</th>
              <th style={thStyle} title={`Klingeln: ${settings.klingeln.toFixed(2)}€`}>Klingeln</th>
              <th style={thStyle} title={`Verlorene Spiele: ${settings.verloreneSpiele.toFixed(2)}€`}>Verl. Spl.</th>
              <th style={thStyle} title={`Kugel bringen: ${settings.kugelBringen.toFixed(2)}€`}>Kug.bring.</th>
              <th style={thStyle} title={`Gewarnt: ${settings.gewarnt.toFixed(2)}€`}>Gewarnt</th>
              <th style={thStyle} title={`Zu spät: ${settings.zuSpaet.toFixed(2)}€/15min`}>Zu spät</th>
              <th style={{ ...thStyle, backgroundColor: "#2d5fa0" }}>Lustwurf<br/>¼R</th>
              <th style={{ ...thStyle, backgroundColor: "#2d5fa0" }}>Schnaps<br/>½R</th>
              <th style={{ ...thStyle, backgroundColor: "#2d5fa0" }}>Alle 9<br/>½R</th>
              <th style={{ ...thStyle, backgroundColor: "#2d5fa0" }}>Kug.fall.<br/>1R</th>
              <th style={{ ...thStyle, backgroundColor: "#2d5fa0" }}>Kug.einh.<br/>½R</th>
              <th style={{ ...thStyle, backgroundColor: "#1e6b3c" }}>+Runden</th>
              <th style={{ ...thStyle, backgroundColor: "#1e6b3c" }}>Übertr.R.</th>
              <th style={{ ...thStyle, backgroundColor: "#1e6b3c" }}>Offen R.</th>
              <th style={{ ...thStyle, backgroundColor: "#b45309" }} title={`${settings.rundenbetrag.toFixed(2)} € pro Runde`}>Runden €</th>
              <th style={thStyle}>Holz</th>
              <th style={{ ...thStyle, backgroundColor: "#b45309" }}>Gesamt €</th>
              <th style={{ ...thStyle, backgroundColor: "#b45309" }}>Bezahlt</th>
              <th style={{ ...thStyle, backgroundColor: "#b45309" }}>Übertrag</th>
            </tr>
            <tr style={{ backgroundColor: "#f0f4fa" }}>
              <td colSpan={2} style={{ ...tdStyle, fontSize: "0.7rem", color: "#888", textAlign: "left", paddingLeft: "0.7rem" }}>
                Grundbetrag {settings.grundbetrag.toFixed(2)}€ · Fehlen +{settings.fehlenUnentsch.toFixed(2)}€ · 1 Runde = {settings.rundenbetrag.toFixed(2)}€
              </td>
              <td style={{ ...tdStyle, fontSize: "0.7rem", color: "#888" }}>×{settings.pumpen.toFixed(2)}€</td>
              <td style={{ ...tdStyle, fontSize: "0.7rem", color: "#888" }}>×{settings.klingeln.toFixed(2)}€</td>
              <td style={{ ...tdStyle, fontSize: "0.7rem", color: "#888" }}>×{settings.verloreneSpiele.toFixed(2)}€</td>
              <td style={{ ...tdStyle, fontSize: "0.7rem", color: "#888" }}>×{settings.kugelBringen.toFixed(2)}€</td>
              <td style={{ ...tdStyle, fontSize: "0.7rem", color: "#888" }}>×{settings.gewarnt.toFixed(2)}€</td>
              <td style={{ ...tdStyle, fontSize: "0.7rem", color: "#888" }}>×{settings.zuSpaet.toFixed(2)}€</td>
              <td colSpan={5} style={{ ...tdStyle, fontSize: "0.7rem", color: "#888" }}></td>
              <td colSpan={4} style={{ ...tdStyle, fontSize: "0.7rem", color: "#888" }}></td>
              <td colSpan={4} style={{ ...tdStyle, fontSize: "0.7rem", color: "#888" }}></td>
            </tr>
          </thead>
          <tbody>
            {(data.eintraege || []).map((e, idx) => (
              <MitgliedZeile key={e.name} eintrag={e} idx={idx} onChange={eintraegeAktualisieren} settings={settings} />
            ))}
          </tbody>
          <tfoot>
            <tr style={{ backgroundColor: "#f0f4fa", fontWeight: 700 }}>
              <td colSpan={18} style={{ ...tdStyle, textAlign: "right", paddingRight: "1rem" }}>Gesamt:</td>
              <td style={{ ...tdStyle, fontWeight: 700, color: "#1a3558" }}>{sumGeld.toFixed(2)} €</td>
              <td colSpan={2}></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div style={{ ...s.card, marginTop: "0.8rem" }}>
        <label style={s.label}>Bemerkungen</label>
        <textarea value={data.bemerkungen || ""} onChange={(e) => { setData((d) => ({ ...d, bemerkungen: e.target.value })); setGespeichert(false); }}
          placeholder="Sonstige Notizen..." style={{ width: "100%", minHeight: "60px", padding: "0.5rem", fontSize: "0.88rem", border: "1px solid #dadce0", borderRadius: "6px", resize: "vertical", boxSizing: "border-box" }} />
      </div>
    </div>
  );
}

// ── Leerer Druckzettel ────────────────────────────────────────────────────────
function DruckLeerzettel({ mitglieder, settings, datum }) {
  const drucken = () => {
    const aktiveMitglieder = datum
      ? mitglieder.filter((m) => isAktivAm(m, datum))
      : mitglieder.filter((m) => !m.bis);
    const maxNameLen = Math.max(...aktiveMitglieder.map((m) => m.name.length), 8);
    const nameBreite = Math.min(Math.max(maxNameLen * 7, 60), 120);
    const zeilen = aktiveMitglieder.map((m) => `
      <tr>
        <td style="border:1px solid #333;padding:4px 6px;font-size:11px;font-weight:bold;width:${nameBreite}px">${m.name}</td>
        ${Array(17).fill('<td style="border:1px solid #333;padding:4px 6px;width:35px"></td>').join("")}
      </tr>`).join("");
    const strafen = [
      `Grundbetrag: ${settings.grundbetrag.toFixed(2)} €`,
      `Fehlen unentsch.: +${settings.fehlenUnentsch.toFixed(2)} €`,
      `Pumpen: ${settings.pumpen.toFixed(2)} €/Stk`,
      `Klingeln: ${settings.klingeln.toFixed(2)} €`,
      `Verl. Spiele: ${settings.verloreneSpiele.toFixed(2)} €`,
      `Kug. bringen: ${settings.kugelBringen.toFixed(2)} €`,
      `Gewarnt: ${settings.gewarnt.toFixed(2)} €`,
      `Zu spät: ${settings.zuSpaet.toFixed(2)} €/15min`,
      `1 Runde = ${settings.rundenbetrag.toFixed(2)} €`,
    ].join(" · ");
    const fenster = window.open("", "_blank");
    fenster.document.write(`<html><head><title>Leerer Kegelzettel</title>
      <style>
        @page { size: A4 landscape; margin: 1cm }
        body { font-family: Arial, sans-serif; margin: 0 }
        table { border-collapse: collapse; width: 100% }
        th { background: #1a3558; color: white; font-size: 9px; border: 1px solid #333; padding: 3px 4px; text-align: center }
        .info { display: flex; gap: 20px; margin-bottom: 6px; font-size: 10px }
        .strafen { margin-top: 6px; font-size: 8px; color: #555; border-top: 1px solid #ccc; padding-top: 4px }
      </style></head><body>
      <div class="info">
        <strong>🎳 Kegelauswertung</strong>
        <span>Datum: ___________________</span>
        <span>Kegelmotto: ___________________</span>
        <span>Kegelmutti: ___________________</span>
      </div>
      <table>
        <thead><tr>
          <th>Name</th>
          <th>Gesamt €</th><th>Bezahlt</th><th>Übertrag</th>
          <th>Grundb.<br>${settings.grundbetrag.toFixed(2)}€</th>
          <th>Fehlen<br>(ue/e)</th>
          <th>Pumpen<br>${settings.pumpen.toFixed(2)}€</th>
          <th>Klingeln<br>${settings.klingeln.toFixed(2)}€</th>
          <th>Verl.Spl.<br>${settings.verloreneSpiele.toFixed(2)}€</th>
          <th>Kug.bring.<br>${settings.kugelBringen.toFixed(2)}€</th>
          <th>Gewarnt<br>${settings.gewarnt.toFixed(2)}€</th>
          <th>Zu spät<br>${settings.zuSpaet.toFixed(2)}€</th>
          <th>Lust-wurf<br>¼R</th>
          <th>Schnaps<br>½R</th>
          <th>Alle 9<br>½R</th>
          <th>Kug.fall.<br>1R</th>
          <th>Kug.einh.<br>½R</th>
          <th>Holz<br>Königsk.</th>
        </tr></thead>
        <tbody>${zeilen}</tbody>
      </table>
      <div class="strafen">${strafen}</div>
      </body></html>`);
    fenster.document.close();
    fenster.print();
  };
  return <button style={s.btn} onClick={drucken}>🖨️ Leeren Zettel drucken</button>;
}

// ── Jahresauswertung ──────────────────────────────────────────────────────────
function JahresauswertungTab({ sessions, settings }) {
  const saisonSessions = sessions;
  const stats = {};
  saisonSessions.forEach((sess) => {
    (sess.eintraege || []).forEach((e) => {
      if (!stats[e.name]) stats[e.name] = { name: e.name, teilnahmen: 0, pumpen: 0, klingeln: 0, verloreneSpiele: 0, lustwurf: 0, kugelBringen: 0, schnappszahl: 0, alleNeune: 0, gewarnt: 0, zuSpaet: 0, kugelFallen: 0, kugelEinholen: 0, holzzahl: 0, neueRunden: 0, gesamtGeld: 0 };
      if (e.status === "abwesend") return;
      const st = stats[e.name];
      st.teilnahmen++;
      ["pumpen","klingeln","verloreneSpiele","lustwurf","kugelBringen","schnappszahl","alleNeune","gewarnt","zuSpaet","kugelFallen","kugelEinholen"].forEach((f) => { st[f] += Number(e[f]) || 0; });
      st.holzzahl += Number(e.holzzahl) || 0;
      st.neueRunden += berechneNeueRunden(e);
      st.gesamtGeld += berechneGeld(e, settings);
    });
  });
  const liste = Object.values(stats).filter((s) => s.teilnahmen > 0);
  liste.sort((a, b) => b.holzzahl - a.holzzahl);

  return (
    <div>
      <div style={{ display: "flex", gap: "0.7rem", alignItems: "center", marginBottom: "1rem" }}>
        <div style={s.h2}>📊 Jahresauswertung</div>
        <span style={{ fontSize: "0.82rem", color: "#888" }}>{saisonSessions.length} Kegel-Abende im Zeitraum</span>
      </div>
      {liste.length === 0 ? (
        <p style={{ color: "#aaa", textAlign: "center" }}>Keine Daten im gewählten Zeitraum.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", backgroundColor: "white", borderRadius: "8px", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
            <thead>
              <tr>
                {["#","Name","Teiln.","Pumpen","Klingeln","Verl.Spl.","Lustwurf","Kug.bring.","Schnaps","Alle 9","Gewarnt","Zu spät","Kug.fall.","Kug.einh.","Runden","Holzzahl ∑","Gesamt €"].map((h, i) => (
                  <th key={i} style={{ ...thStyle, backgroundColor: h === "Holzzahl ∑" ? "#1e6b3c" : "#1a3558" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {liste.map((st, i) => (
                <tr key={st.name} style={{ backgroundColor: i % 2 === 0 ? "white" : "#fafafa" }}>
                  <td style={{ ...tdStyle, fontWeight: 700, color: "#888" }}>{i + 1}</td>
                  <td style={{ ...tdStyle, fontWeight: 700, textAlign: "left", paddingLeft: "0.7rem" }}>{st.name}</td>
                  <td style={tdStyle}>{st.teilnahmen}</td>
                  <td style={tdStyle}>{st.pumpen}</td>
                  <td style={tdStyle}>{st.klingeln}</td>
                  <td style={tdStyle}>{st.verloreneSpiele}</td>
                  <td style={tdStyle}>{st.lustwurf}</td>
                  <td style={tdStyle}>{st.kugelBringen}</td>
                  <td style={tdStyle}>{st.schnappszahl}</td>
                  <td style={tdStyle}>{st.alleNeune}</td>
                  <td style={tdStyle}>{st.gewarnt}</td>
                  <td style={tdStyle}>{st.zuSpaet}</td>
                  <td style={tdStyle}>{st.kugelFallen}</td>
                  <td style={tdStyle}>{st.kugelEinholen}</td>
                  <td style={{ ...tdStyle, color: st.neueRunden > 0 ? "#c5221f" : "#333" }}>{st.neueRunden.toFixed(2)}</td>
                  <td style={{ ...tdStyle, fontWeight: 700, color: "#1e6b3c", fontSize: "1rem" }}>{st.holzzahl}</td>
                  <td style={{ ...tdStyle, fontWeight: 700 }}>{st.gesamtGeld.toFixed(2)} €</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Referenztabelle / Einstellungen ──────────────────────────────────────────
function EinstellungenPanel({ settings, onSpeichern, onSchliessen }) {
  const [werte, setWerte] = useState({ ...settings });
  const felder = [
    ["grundbetrag", "Grundbetrag (€)"],
    ["fehlenUnentsch", "Fehlen unentschuldigt (€)"],
    ["pumpen", "Pumpen (€/Stk)"],
    ["klingeln", "Klingeln (€)"],
    ["verloreneSpiele", "Verlorene Spiele (€)"],
    ["kugelBringen", "Kugel bringen (€)"],
    ["gewarnt", "Gewarnt (€)"],
    ["zuSpaet", "Zu spät kommen (€/15min)"],
    ["rundenbetrag", "Betrag pro Runde (€)"],
  ];
  return (
    <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ backgroundColor: "white", borderRadius: "12px", padding: "1.5rem", width: "380px", maxHeight: "85vh", overflowY: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
        <div style={{ fontWeight: 700, fontSize: "1rem", color: "#1a3558", marginBottom: "1rem" }}>⚙️ Strafen-Referenz</div>
        {felder.map(([key, label]) => (
          <div key={key} style={{ display: "flex", alignItems: "center", gap: "0.8rem", marginBottom: "0.5rem" }}>
            <label style={{ flex: 1, fontSize: "0.85rem" }}>{label}</label>
            <input type="number" step="0.01" min="0" value={werte[key]}
              onChange={(e) => setWerte((w) => ({ ...w, [key]: Number(e.target.value) }))}
              style={{ ...s.input, width: "80px", textAlign: "right" }} />
          </div>
        ))}
        <div style={{ fontSize: "0.75rem", color: "#888", marginTop: "0.5rem", marginBottom: "1rem" }}>
          Lustwurf = ¼ Runde · Schnappszahl = ½ Runde · Alle Neune = ½ Runde · Kugel fallen = 1 Runde · Kugel einholen = ½ Runde
        </div>
        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
          <button style={s.btnSm} onClick={onSchliessen}>Abbrechen</button>
          <button style={s.btnGreen} onClick={() => onSpeichern(werte)}>💾 Speichern</button>
        </div>
      </div>
    </div>
  );
}

// ── Mitglieder-Verwaltung ─────────────────────────────────────────────────────
function MitgliederVerwaltung({ mitglieder, onSpeichern, onSchliessen }) {
  const [liste, setListe] = useState(mitglieder.map((m) => typeof m === "string" ? { name: m, von: "", bis: "" } : { ...m }));
  const [neuName, setNeuName] = useState("");

  const hinzufuegen = () => {
    const n = neuName.trim();
    if (!n || liste.some((m) => m.name === n)) return;
    setListe((l) => [...l, { name: n, von: new Date().toISOString().slice(0, 10), bis: "" }]);
    setNeuName("");
  };

  const update = (idx, field, val) => setListe((l) => l.map((m, i) => i === idx ? { ...m, [field]: val } : m));
  const entfernen = (idx) => setListe((l) => l.filter((_, i) => i !== idx));
  const verschieben = (idx, d) => {
    const neu = [...liste];
    const z = idx + d;
    if (z < 0 || z >= neu.length) return;
    [neu[idx], neu[z]] = [neu[z], neu[idx]];
    setListe(neu);
  };

  return (
    <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ backgroundColor: "white", borderRadius: "12px", padding: "1.5rem", width: "560px", maxHeight: "85vh", overflowY: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
        <div style={{ fontWeight: 700, fontSize: "1rem", color: "#1a3558", marginBottom: "0.5rem" }}>👥 Mitglieder verwalten</div>
        <div style={{ fontSize: "0.75rem", color: "#888", marginBottom: "0.8rem" }}>Von/Bis leer = unbegrenzt aktiv</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 100px 100px 60px", gap: "0.3rem", fontWeight: 600, fontSize: "0.75rem", color: "#555", marginBottom: "0.3rem", padding: "0 0.3rem" }}>
          <span>Name</span><span>Mitglied von</span><span>Bis (leer = aktiv)</span><span></span>
        </div>
        {liste.map((m, idx) => (
          <div key={idx} style={{ display: "grid", gridTemplateColumns: "1fr 100px 100px auto", gap: "0.3rem", marginBottom: "0.3rem", alignItems: "center" }}>
            <span style={{ fontSize: "0.88rem", padding: "0.3rem 0.5rem", backgroundColor: m.bis ? "#fce8e6" : "#e8f5e9", borderRadius: "5px" }}>
              {m.name}
            </span>
            <input type="date" style={{ ...s.input, fontSize: "0.75rem" }} value={m.von || ""}
              onChange={(e) => update(idx, "von", e.target.value)} />
            <input type="date" style={{ ...s.input, fontSize: "0.75rem" }} value={m.bis || ""}
              onChange={(e) => update(idx, "bis", e.target.value)} />
            <div style={{ display: "flex", gap: "0.2rem" }}>
              <button style={s.btnSm} onClick={() => verschieben(idx, -1)} disabled={idx === 0}>↑</button>
              <button style={s.btnSm} onClick={() => verschieben(idx, 1)} disabled={idx === liste.length - 1}>↓</button>
              <button style={s.btnDanger} onClick={() => entfernen(idx)}>✕</button>
            </div>
          </div>
        ))}
        <div style={{ display: "flex", gap: "0.4rem", marginTop: "0.8rem" }}>
          <input style={{ ...s.input, flex: 1 }} placeholder="Neuer Name..." value={neuName}
            onChange={(e) => setNeuName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && hinzufuegen()} />
          <button style={s.btn} onClick={hinzufuegen}>+ Hinzufügen</button>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem", justifyContent: "flex-end" }}>
          <button style={s.btnSm} onClick={onSchliessen}>Abbrechen</button>
          <button style={s.btnGreen} onClick={() => onSpeichern(liste)}>💾 Speichern</button>
        </div>
      </div>
    </div>
  );
}

// ── Foto-Erfassung ────────────────────────────────────────────────────────────
function FotoErfassungTab({ mitglieder, onErstellen }) {
  const [freitext, setFreitext] = useState("");
  const [bild, setBild] = useState(null);
  const [bildData, setBildData] = useState(null);
  const [laden, setLaden] = useState(false);
  const [ergebnis, setErgebnis] = useState(null);
  const [fehler, setFehler] = useState("");

  const aktiveMitglieder = mitglieder.filter((m) => !m.bis);

  const bildLaden = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBild(file);
    const reader = new FileReader();
    reader.onload = (ev) => setBildData(ev.target.result);
    reader.readAsDataURL(file);
  };

  const analyseStarten = async () => {
    if (!bild && !freitext.trim()) return;
    setLaden(true); setFehler(""); setErgebnis(null);
    try {
      const namen = aktiveMitglieder.map((m) => m.name).join(", ");
      const prompt = `Du analysierst einen Kegel-Zettel. Mitglieder: ${namen}.
Extrahiere für jedes Mitglied: pumpen, klingeln, verloreneSpiele, lustwurf, kugelBringen, schnappszahl, alleNeune, gewarnt, zuSpaet, kugelFallen, kugelEinholen, holzzahl (Zahlen, 0 wenn nicht vorhanden).
Auch: status (anwesend/unentsch/entsch/abwesend).
Antworte NUR als JSON-Array: [{"name":"...","status":"anwesend","pumpen":3,...}]`;
      const messages = [{ role: "user", content: bild ? [
        { type: "text", text: prompt },
        { type: "image_url", image_url: { url: bildData } },
      ] : [{ type: "text", text: `${prompt}\n\nFreitext: ${freitext}` }] }];
      const { data } = await openaiChat({ model: "gpt-4o", messages, max_tokens: 2000 });
      const text = data.content || "";
      const match = text.match(/\[[\s\S]*\]/);
      if (match) setErgebnis(JSON.parse(match[0]));
      else setFehler("Konnte keine Daten extrahieren.");
    } catch (err) { setFehler("Fehler: " + err.message); }
    setLaden(false);
  };

  const alsNeuenZettelSpeichern = () => {
    if (!ergebnis) return;
    const heute = new Date().toISOString().slice(0, 10);
    const eintraege = aktiveMitglieder.map((m) => {
      const gefunden = ergebnis.find((r) => r.name?.toLowerCase().includes(m.name.toLowerCase().split(" ")[0]));
      if (gefunden) {
        const e = { ...leerEintrag(m.name), ...gefunden, name: m.name };
        e.neueRunden = berechneNeueRunden(e);
        e.offeneRunden = e.neueRunden;
        return e;
      }
      return leerEintrag(m.name);
    });
    onErstellen({ datum: heute, eintraege, motto: "", kegelmutti: "", kassiererin: "", schriftfuehrer: "", rundenschreier: "", bemerkungen: "" });
  };

  return (
    <div>
      <div style={s.h2}>📸 Kegelzettel erfassen</div>
      <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
        <div style={{ ...s.card, flex: 1, minWidth: "280px" }}>
          <div style={{ fontWeight: 600, marginBottom: "0.5rem" }}>Foto hochladen</div>
          <input type="file" accept="image/*" onChange={bildLaden} style={{ marginBottom: "0.5rem" }} />
          {bildData && <img src={bildData} alt="Kegelzettel" style={{ maxWidth: "100%", maxHeight: "300px", objectFit: "contain", borderRadius: "6px", border: "1px solid #eee" }} />}
        </div>
        <div style={{ ...s.card, flex: 1, minWidth: "280px" }}>
          <div style={{ fontWeight: 600, marginBottom: "0.5rem" }}>Freie Mitschrift</div>
          <textarea value={freitext} onChange={(e) => setFreitext(e.target.value)}
            placeholder={"Keily: Pumpen 3, Klingeln 1, Holz 42\nStefan: fehlt entschuldigt\n..."}
            style={{ width: "100%", minHeight: "200px", padding: "0.5rem", fontSize: "0.88rem", border: "1px solid #dadce0", borderRadius: "6px", resize: "vertical", boxSizing: "border-box" }} />
        </div>
      </div>
      <div style={{ display: "flex", gap: "0.7rem", marginTop: "0.5rem" }}>
        <button style={{ ...s.btn, opacity: laden ? 0.6 : 1 }} onClick={analyseStarten} disabled={laden || (!bild && !freitext.trim())}>
          {laden ? "⏳ Analysiere..." : "🤖 KI-Analyse starten"}
        </button>
      </div>
      {fehler && <p style={{ color: "#ea4335", marginTop: "0.5rem" }}>{fehler}</p>}
      {ergebnis && (
        <div style={{ ...s.card, marginTop: "0.8rem" }}>
          <div style={{ fontWeight: 600, marginBottom: "0.5rem", color: "#34a853" }}>✅ Erkannte Daten</div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%" }}>
              <thead>
                <tr>{["Name","Status","Pumpen","Klingeln","Verl.","Lustwurf","Kug.bring.","Schnaps","Alle 9","Fallen","Einh.","Holz"].map((h) => (
                  <th key={h} style={{ ...thStyle, padding: "0.3rem 0.5rem" }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {ergebnis.map((r) => (
                  <tr key={r.name}>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{r.name}</td>
                    <td style={tdStyle}>{r.status || "anwesend"}</td>
                    {["pumpen","klingeln","verloreneSpiele","lustwurf","kugelBringen","schnappszahl","alleNeune","kugelFallen","kugelEinholen","holzzahl"].map((f) => (
                      <td key={f} style={tdStyle}>{r[f] || 0}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button style={{ ...s.btnGreen, marginTop: "0.7rem" }} onClick={alsNeuenZettelSpeichern}>✅ Als neuen Kegelzettel übernehmen</button>
        </div>
      )}
    </div>
  );
}

// ── Von-Bis Filter ────────────────────────────────────────────────────────────
function VonBisFilter({ filter, onChange }) {
  const aktuellesJahr = new Date().getFullYear();
  const jahre = Array.from({ length: aktuellesJahr - 1997 }, (_, i) => 1998 + i).reverse();
  const setVon = (field, val) => onChange({ ...filter, von: { ...filter.von, [field]: Number(val) } });
  const setBis = (field, val) => onChange({ ...filter, bis: { ...filter.bis, [field]: Number(val) } });
  return (
    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap", padding: "0.6rem 0.8rem", backgroundColor: "#f0f4fa", borderRadius: "8px", marginBottom: "0.8rem" }}>
      <span style={{ fontSize: "0.82rem", color: "#555", fontWeight: 600 }}>Zeitraum:</span>
      <span style={{ fontSize: "0.82rem", color: "#555" }}>von</span>
      <select style={s.select} value={filter.von.monat} onChange={(e) => setVon("monat", e.target.value)}>
        {MONATE.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
      </select>
      <select style={s.select} value={filter.von.jahr} onChange={(e) => setVon("jahr", e.target.value)}>
        {jahre.map((j) => <option key={j} value={j}>{j}</option>)}
      </select>
      <span style={{ fontSize: "0.82rem", color: "#555" }}>bis</span>
      <select style={s.select} value={filter.bis.monat} onChange={(e) => setBis("monat", e.target.value)}>
        {MONATE.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
      </select>
      <select style={s.select} value={filter.bis.jahr} onChange={(e) => setBis("jahr", e.target.value)}>
        {jahre.map((j) => <option key={j} value={j}>{j}</option>)}
      </select>
    </div>
  );
}

// ── Hauptkomponente ───────────────────────────────────────────────────────────
export default function Kegeln({ user }) {
  const [sessions, setSessions] = useState([]);
  const [mitglieder, setMitglieder] = useState(MITGLIEDER_DEFAULT);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [aktiverTab, setAktivTab] = useState("zettel");
  const [offeneSession, setOffeneSession] = useState(null);
  const [filter, setFilter] = useState(ladeFilterAusStorage);
  const [mitgliederOffen, setMitgliederOffen] = useState(false);
  const [einstellungenOffen, setEinstellungenOffen] = useState(false);

  useEffect(() => {
    const q = query(collection(db, "kegeln_sessions"), where("uid", "==", user.uid));
    return onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.datum || "").localeCompare(a.datum || ""));
      setSessions(data);
    });
  }, [user.uid]);

  useEffect(() => {
    return onSnapshot(doc(db, "kegeln_mitglieder", user.uid), (snap) => {
      if (snap.exists() && snap.data().liste?.length > 0) {
        const raw = snap.data().liste;
        setMitglieder(raw.map((m) => typeof m === "string" ? { name: m, von: "", bis: "" } : m));
      }
    });
  }, [user.uid]);

  useEffect(() => {
    return onSnapshot(doc(db, "kegeln_settings", user.uid), (snap) => {
      if (snap.exists()) setSettings({ ...DEFAULT_SETTINGS, ...snap.data() });
    });
  }, [user.uid]);

  useEffect(() => {
    try { localStorage.setItem("kegeln_filter", JSON.stringify(filter)); } catch {}
  }, [filter]);

  const mitgliederSpeichern = async (liste) => {
    await setDoc(doc(db, "kegeln_mitglieder", user.uid), { liste });
    setMitgliederOffen(false);
  };

  const settingsSpeichern = async (werte) => {
    await setDoc(doc(db, "kegeln_settings", user.uid), werte);
    setEinstellungenOffen(false);
  };

  const neueSession = async () => {
    const heute = new Date().toISOString().slice(0, 10);
    const aktiveMitglieder = mitglieder.filter((m) => isAktivAm(m, heute));
    const eintraege = aktiveMitglieder.map((m) => leerEintrag(m.name));
    const ref = await addDoc(collection(db, "kegeln_sessions"), {
      uid: user.uid, datum: heute, saison: getSaison(heute),
      motto: "", kegelmutti: "", kassiererin: "", schriftfuehrer: "", rundenschreier: "",
      bemerkungen: "", eintraege,
    });
    setOffeneSession({ id: ref.id, datum: heute, saison: getSaison(heute), eintraege, motto: "", kegelmutti: "", kassiererin: "", schriftfuehrer: "", rundenschreier: "", bemerkungen: "" });
    setAktivTab("zettel");
  };

  const sessionSpeichern = async (data) => {
    const { id, ...rest } = data;
    rest.saison = getSaison(rest.datum || new Date().toISOString());
    await updateDoc(doc(db, "kegeln_sessions", id), rest);
    setOffeneSession({ ...data, saison: rest.saison });
  };

  const sessionLoeschen = async (id) => {
    if (!window.confirm("Kegelzettel wirklich löschen?")) return;
    await deleteDoc(doc(db, "kegeln_sessions", id));
  };

  const gefilterteSessions = sessions.filter((s) => datumImBereich(s.datum, filter.von, filter.bis));

  if (aktiverTab === "zettel" && offeneSession) {
    return (
      <div style={s.page}>
        <KegelzettelDetail session={offeneSession} onSpeichern={sessionSpeichern} onZurueck={() => setOffeneSession(null)} settings={settings} />
      </div>
    );
  }

  return (
    <div style={s.page}>
      {mitgliederOffen && <MitgliederVerwaltung mitglieder={mitglieder} onSpeichern={mitgliederSpeichern} onSchliessen={() => setMitgliederOffen(false)} />}
      {einstellungenOffen && <EinstellungenPanel settings={settings} onSpeichern={settingsSpeichern} onSchliessen={() => setEinstellungenOffen(false)} />}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", flexWrap: "wrap", gap: "0.5rem" }}>
        <div style={{ fontWeight: 700, color: "#1a3558", fontSize: "1.3rem" }}>🎳 Kegeln</div>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <button style={s.btnSm} onClick={() => setEinstellungenOffen(true)}>⚙️ Strafen-Referenz</button>
          <button style={s.btnSm} onClick={() => setMitgliederOffen(true)}>👥 Mitglieder</button>
          <DruckLeerzettel mitglieder={mitglieder} settings={settings} />
          <button style={s.btnGreen} onClick={neueSession}>+ Neuer Kegelabend</button>
        </div>
      </div>

      <div style={{ display: "flex", gap: "0.4rem", marginBottom: "1rem" }}>
        {[["zettel","📋 Kegelzettel"],["auswertung","📊 Jahresauswertung"],["erfassung","📸 Erfassung"]].map(([id, label]) => (
          <button key={id} style={{ ...s.tab, ...(aktiverTab === id ? s.tabAktiv : {}) }} onClick={() => setAktivTab(id)}>{label}</button>
        ))}
      </div>

      <VonBisFilter filter={filter} onChange={setFilter} />

      {aktiverTab === "zettel" && (
        <div>
          <div style={{ fontSize: "0.82rem", color: "#888", marginBottom: "0.5rem" }}>
            {gefilterteSessions.length} Kegelabend{gefilterteSessions.length !== 1 ? "e" : ""} im Zeitraum
          </div>
          {gefilterteSessions.length === 0 ? (
            <p style={{ color: "#aaa", textAlign: "center", marginTop: "2rem" }}>
              Keine Einträge im gewählten Zeitraum.<br />
              <span style={{ fontSize: "0.82rem" }}>Zeitraum anpassen oder „+ Neuer Kegelabend" klicken.</span>
            </p>
          ) : (
            <div>
              {gefilterteSessions.map((sess) => {
                const teilnehmer = (sess.eintraege || []).filter((e) => e.status === "anwesend").length;
                const sumGeld = (sess.eintraege || []).reduce((sum, e) => sum + berechneGeld(e, settings), 0);
                return (
                  <div key={sess.id} style={{ ...s.card, display: "flex", alignItems: "center", gap: "0.8rem", cursor: "pointer" }}
                    onClick={() => { setOffeneSession(sess); setAktivTab("zettel"); }}>
                    <div style={{ fontSize: "1.5rem" }}>🎳</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, color: "#1a3558" }}>
                        {sess.datum ? new Date(sess.datum).toLocaleDateString("de-DE", { weekday: "short", year: "numeric", month: "long", day: "numeric" }) : "—"}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#666" }}>
                        {sess.motto && <span>„{sess.motto}" · </span>}
                        {teilnehmer} Teilnehmer · {sumGeld.toFixed(2)} €
                      </div>
                    </div>
                    <span style={s.tag}>Saison {getSaison(sess.datum)}</span>
                    <button style={s.btnDanger} onClick={(e) => { e.stopPropagation(); sessionLoeschen(sess.id); }}>🗑️</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {aktiverTab === "auswertung" && <JahresauswertungTab sessions={gefilterteSessions} settings={settings} />}
      {aktiverTab === "erfassung" && (
        <FotoErfassungTab mitglieder={mitglieder} onErstellen={async (data) => {
          const ref = await addDoc(collection(db, "kegeln_sessions"), { uid: user.uid, ...data, saison: getSaison(data.datum) });
          setOffeneSession({ id: ref.id, ...data });
          setAktivTab("zettel");
        }} />
      )}
    </div>
  );
}
